import React, { useState, useEffect, useCallback } from "react";
import { Table, Spinner, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from '../../../AxiosInstance.tsx';
import { useApiLoader } from "../../../hooks/useApiLoader.tsx";
import { API_ENDPOINTS } from '../../utils.ts';
import ShortMain from "../../../images/short-icon-main.svg"
import ShortUp from "../../../images/short-icon-top.svg"
import ShortDown from "../../../images/short-icon-bottom.svg"
import "../../../App.css"

interface Gift {
  _id: string;
  orderId: string;
  orderDate: string;
  recipient: string;
  relationship: string;
  occasion: string;
  giftType: string;
  recurring: string;
  deliveryDate: string;
  giftStatus: string;
}

interface GiftProps {
  userId: string;
  isActive: boolean;
}

interface SortConfig {
  sortBy: string;
  sortOrder: 'asc' | 'desc' | '';
}

const Gift: React.FC<GiftProps> = ({ userId, isActive }) => {
  const { withLoader } = useApiLoader();
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    sortBy: '',
    sortOrder: ''
  });

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';

      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();

      return `${month}/${day}/${year}`;
    } catch (error) {
      return 'N/A';
    }
  };

  const capitalizeText = (text: string) => {
    if (!text) return 'N/A';
    // Capitalize first letter of each word
    return text
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getGiftTypeBadge = (giftType: string) => {
    const type = giftType?.toLowerCase();
    let badgeClass = 'round-badge badge bg-secondary';

    switch (type) {
      case 'physical':
        badgeClass = 'round-badge badge bg-warning text-dark';
        break;
      case 'coupon':
        badgeClass = 'round-badge badge bg-primary-subtle text-primary';
        break;
      case 'monetary':
        badgeClass = 'round-badge badge bg-success';
        break;
      case 'message':
        badgeClass = 'round-badge badge bg-secondary';
        break;
      default:
        badgeClass = 'round-badge badge bg-secondary';
    }

    return <span className={badgeClass}>{giftType}</span>;
  };

  const getGiftStatusBadge = (status: string) => {
    const statusLower = status?.toLowerCase();
    let color = 'secondary';

    switch (statusLower) {
      case 'upcoming':
        color = 'or-badge';
        break;
      case 'processing':
        color = 'blu-badge';
        break;
      case 'shipped':
        color = 'gre-badge';
        break;
      case 'delivered':
        color = 'succ-badge';
        break;
      default:
        color = 'secondary';
    }

    return <Badge bg={color}>{status || 'N/A'}</Badge>;
  };

  const fetchUserGifts = useCallback(async (
    sortField: string = '',
    sortDirection: string = ''
  ) => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);

      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        toast.error('Authentication required');
        return;
      }

      // Prepare request parameters
      const params: any = {};

      // Add sorting parameters
      if (sortField && sortDirection) {
        params.sortBy = sortField;
        params.sortOrder = sortDirection;
      }

      const response = await withLoader(
        () => axiosInstance.get(`${API_ENDPOINTS.GET_ALL_USER_GIFTS}/${userId}`, {
          params,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          }
        }),
        'Loading gifts...'
      );

      if (response.data.status === 'Success') {
        setGifts(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to fetch gifts');
        setGifts([]);
      }
    } catch (error: any) {
      console.error('Error fetching user gifts:', error);
      setError(error.response?.data?.message || 'Failed to fetch gifts');
      setGifts([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isActive && userId) {
      fetchUserGifts(sortConfig.sortBy, sortConfig.sortOrder);
    }
  }, [userId, isActive, sortConfig.sortBy, sortConfig.sortOrder, fetchUserGifts]);

  // Handle sorting
  const handleSort = useCallback((field: string) => {
    let newSortOrder: 'asc' | 'desc' = 'asc';

    if (sortConfig.sortBy === field) {
      if (sortConfig.sortOrder === 'asc') {
        newSortOrder = 'desc';
      } else if (sortConfig.sortOrder === 'desc') {
        // Reset sorting - no sort
        setSortConfig({ sortBy: '', sortOrder: '' });
        fetchUserGifts();
        return;
      }
    }

    setSortConfig({ sortBy: field, sortOrder: newSortOrder });
    fetchUserGifts(field, newSortOrder);
  }, [sortConfig.sortBy, sortConfig.sortOrder, fetchUserGifts]);

  // Get sort icon for sortable columns
  const getSortIcon = useCallback((field: string) => {
    if (sortConfig.sortBy !== field) {
      return (
        <span className='short-icon'>
          <img src={ShortMain} alt='' />
        </span>
      );
    }

    if (sortConfig.sortOrder === 'asc') {
      return (
        <span className='short-icon'>
          <img src={ShortUp} alt='' />
        </span>
      );
    } else {
      return (
        <span className='short-icon'>
          <img src={ShortDown} alt='' />
        </span>
      );
    }
  }, [sortConfig.sortBy, sortConfig.sortOrder]);

  return (
    <>
      <div className="main-table">
        <div className="table-responsive mt-3">
          <Table hover bordered className="mb-0 custom-payment-table">
            <thead className="table-light">
              <tr>
                <th>Order ID</th>
                <th>Order Date</th>
                <th>Recipient</th>
                <th>Relationship</th>
                <th>Occasion</th>
                <th>Gift Type</th>
                <th
                  onClick={() => handleSort("recurring")}
                  style={{ cursor: "pointer" }}
                >
                  <span
                    style={{ display: "inline-flex", alignItems: "center" }}
                  >
                    Recurring {getSortIcon("recurring")}
                  </span>
                </th>
                <th
                  onClick={() => handleSort("deliveryDate")}
                  style={{ cursor: "pointer" }}
                >
                  <span
                    style={{ display: "inline-flex", alignItems: "center" }}
                  >
                    Delivery Date {getSortIcon("deliveryDate")}
                  </span>
                </th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="text-center py-4">
                    <Spinner color="primary" />
                    <p className="mt-2 mb-0">Loading gifts...</p>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={9} className="text-center py-4">
                    <p className="text-danger mb-0">{error}</p>
                  </td>
                </tr>
              ) : gifts.length > 0 ? (
                gifts.map((gift) => (
                  <tr key={gift._id}>
                    <td>{gift.orderId}</td>
                    <td>{formatDate(gift.orderDate)}</td>
                    <td>{capitalizeText(gift.recipient)}</td>
                    <td>{capitalizeText(gift.relationship)}</td>
                    <td>{capitalizeText(gift.occasion)}</td>
                    <td>{getGiftTypeBadge(gift.giftType)}</td>
                    <td>{capitalizeText(gift.recurring)}</td>
                    <td>{formatDate(gift.deliveryDate)}</td>
                    <td className="info-badge-area">
                      <p className="d-flex align-items-center gap-1 mb-0">
                        {getGiftStatusBadge(gift.giftStatus)}
                      </p>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="text-center py-4">
                    <p className="text-muted mb-0">No gifts found for this user.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </>
  );
};

export default Gift;